import { CSRF_HEADER, type IdpRemovalResult, type RemovalImpact } from "@esi/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe, type LucideIcon, MonitorSmartphone, UserRound, UsersRound } from "lucide-react";
import { useId, useRef, useState } from "react";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccount } from "../account/queries.ts";
import { StepUpDialog } from "../account/StepUpDialog.tsx";
import { ApiError, needsReauth, ok } from "../api-errors.ts";
import { i18n, t } from "../i18n/i18n.ts";
import { adminSettingsPath, Link } from "../router.tsx";
import { useSecureAction } from "../security/step-up.tsx";
import { useSession } from "../session.tsx";
import { settingHash } from "../settings-shell/setting-anchors.ts";
import { useAdminApi } from "./admin-api.tsx";
import { signinKeys } from "./signin-keys.ts";

/**
 * What a row menu asks to end (idp-domain-removal D9): an identity provider, a verified domain
 * (both stated from the server's preview, typed to confirm, after a recent sign-in), or an
 * unverified claim (a short confirm: it routes nobody).
 */
export type RemovalTarget =
  | {
      kind: "idp";
      id: string;
      name: string;
      /** Removed already: only its unfinished clean-up is left to retry. */
      cleanup?: boolean;
    }
  | { kind: "domain"; id: string; name: string; verified: boolean };

/**
 * The typed confirmation, compared as the server does: an IdP's display name exactly (trimmed),
 * a domain in any case.
 */
export function confirmationMatches(target: RemovalTarget, typed: string): boolean {
  return target.kind === "idp"
    ? typed.trim() === target.name.trim()
    : typed.trim().toLowerCase() === target.name.trim().toLowerCase();
}

const isRefusal = (error: unknown) =>
  error instanceof ApiError && error.code === "last_sign_in_method";

/**
 * The confirmation of a removal or release. Every figure and sentence comes from the server's
 * preview; the client guesses none of them. A `reauth_required` answer runs the existing step-up
 * and retries; a removal whose broker clean-up did not finish stays open with Retry.
 */
export function RemovalDialog(props: {
  target: RemovalTarget | null;
  onClose: () => void;
  onDone: (notice: string) => void;
  /** The removal happened but its clean-up did not finish (the title then says so). */
  onPending?: () => void;
  returnFocus?: () => HTMLElement | null;
}) {
  // The last target stays shown while the dialog fades out, never an empty, untitled box.
  const last = useRef<RemovalTarget | null>(null);
  if (props.target) {
    last.current = props.target;
  }
  const target = props.target ?? last.current;
  return (
    <AppDialog
      open={props.target !== null}
      onClose={props.onClose}
      returnFocus={props.returnFocus}
      title={target ? title(target) : ""}
      description={target ? lead(target) : undefined}
    >
      {target ? (
        // Keyed: another target starts from an empty confirmation and a fresh preview.
        <RemovalBody key={`${target.kind}:${target.id}`} {...props} target={target} />
      ) : null}
    </AppDialog>
  );
}

function title(target: RemovalTarget) {
  if (target.kind === "idp" && target.cleanup) {
    return t("signin.removal.cleanupTitle", { name: target.name });
  }
  if (target.kind === "idp") {
    return t("signin.removal.idpTitle", { name: target.name });
  }
  return target.verified
    ? t("signin.removal.domainTitle", { domain: target.name })
    : t("signin.removal.claimTitle", { domain: target.name });
}

function lead(target: RemovalTarget) {
  if (target.kind === "idp" && target.cleanup) {
    return undefined;
  }
  if (target.kind === "idp") {
    return t("signin.removal.idpLead", { name: target.name });
  }
  return target.verified
    ? t("signin.removal.domainLead", { domain: target.name })
    : t("signin.removal.claimLead", { domain: target.name });
}

function RemovalBody(props: {
  target: RemovalTarget;
  onClose: () => void;
  onDone: (notice: string) => void;
  onPending?: () => void;
}) {
  const { target } = props;
  const id = useId();
  const { admin, base } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const account = useAccount();
  const stepUp = useSecureAction();
  const [typed, setTyped] = useState("");
  /** The verified domains kept as ESI-accounts domains (#419, D11), by ID, sorted. */
  const [keep, setKeep] = useState<readonly string[]>([]);
  const [pending, setPending] = useState(target.kind === "idp" && target.cleanup === true);
  /** A retry came back unfinished again: say so, or the click seems to do nothing. */
  const [stillPending, setStillPending] = useState(false);
  /** A kept domain's links (#419, D11), kept across a clean-up retry that no longer reports them. */
  const [links, setLinks] = useState<KeptLinks | null>(null);
  const short = target.kind === "domain" && !target.verified;
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };

  const preview = useQuery({
    // (A clean-up retry has no preview: its IdP is removed already.)
    queryKey: ["admin", "removal-preview", target.kind, target.id, keep],
    queryFn: async (): Promise<RemovalImpact> =>
      target.kind === "idp"
        ? ((await ok(
            await admin["identity-providers"][":id"].removal.$get({
              param: { id: target.id },
              query: keep.length > 0 ? { keep: keep.join(",") } : {},
            }),
          )) as RemovalImpact)
        : ((await ok(
            await admin.domains[":domainId"].release.$get({ param: { domainId: target.id } }),
          )) as RemovalImpact),
    enabled: !short && !pending,
    retry: false,
    // Ticking a domain asks again; the last answer stays shown meanwhile (submit waits for it).
    placeholderData: keepPreviousData,
    // A preview is a snapshot for this confirmation only: never served from an older one.
    gcTime: 0,
    staleTime: 0,
  });

  const run = useMutation({
    /** `confirm` absent: a cancelled claim, or the retry of an unfinished clean-up. */
    mutationFn: async (confirm: string | undefined) => {
      const json = confirm === undefined ? {} : { confirm };
      if (target.kind === "idp" && keep.length > 0 && confirm !== undefined) {
        // Its own route (#419, D11): a previous release answers 404, never a plain removal.
        return (await ok(
          await admin["identity-providers"][":id"].remove.$post(
            { param: { id: target.id }, json: { confirm, keepDomains: [...keep] } },
            headers,
          ),
        )) as Partial<IdpRemovalResult>;
      }
      if (target.kind === "idp") {
        return (await ok(
          await admin["identity-providers"][":id"].$delete(
            { param: { id: target.id }, json },
            headers,
          ),
        )) as Partial<IdpRemovalResult>;
      }
      return (await ok(
        await admin.domains[":domainId"].$delete({ param: { domainId: target.id }, json }, headers),
      )) as Partial<IdpRemovalResult>;
    },
    onSettled: () =>
      Promise.all(
        // What a removal changes elsewhere: the directory's sources and groups, the audit trail.
        [signinKeys.identityProviders, ["admin", "directory"], ["admin", "audit"]].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ),
  });

  /** Through the step-up: a `reauth_required` answer asks for it, then runs this again. */
  const submit = (confirm: string | undefined) =>
    stepUp
      .secure(async () => {
        const result = await run.mutateAsync(confirm);
        const sent: KeptLinks | null =
          result.linksSent === undefined
            ? links
            : { linksSent: result.linksSent, linksFailed: result.linksFailed ?? 0 };
        if (result.cleanupPending) {
          setLinks(sent);
          setStillPending(pending);
          setPending(true);
          props.onPending?.();
          return;
        }
        props.onDone(done(target, pending, sent));
      })
      // The refusal is on the mutation (shown below); nothing else to do here.
      .catch(() => undefined);

  const busy = run.isPending;
  const refused = preview.data?.allowed === false || isRefusal(run.error);
  const otherError =
    run.error && !isRefusal(run.error) && !needsReauth(run.error) ? run.error : null;
  const ready =
    short ||
    (preview.data?.allowed === true &&
      !preview.isPlaceholderData &&
      confirmationMatches(target, typed));

  // The step-up needs the account (password or IdP); a failed read says so and retries it,
  // and the held action then runs once the step-up succeeds.
  const accountError =
    stepUp.asking && account.isError ? (
      <Notice
        tone="danger"
        action={
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={account.isFetching}
            onClick={() => void account.refetch()}
          >
            {t("signin.removal.retryAccount")}
          </Button>
        }
      >
        {account.error.message}
      </Notice>
    ) : null;
  /** A step-up is wanted but the account is still being read: nothing to click meanwhile. */
  const waiting = stepUp.asking && account.isPending;
  const stepUpDialog = (
    <StepUpDialog
      // Only once the account is known: a federated admin must never get the password form.
      open={stepUp.asking && account.isSuccess}
      passkeys={account.data?.security.passkeysAvailable ?? false}
      federatedEmail={account.data?.federated ? account.data.email : null}
      returnTo={`${base}/signin`}
      onClose={stepUp.cancel}
      onDone={() => {
        const action = stepUp.take();
        if (action) {
          void stepUp.secure(action).catch(() => undefined);
        }
      }}
    />
  );

  if (pending) {
    return (
      <div className="flex flex-col gap-4">
        <Notice tone="warning" live>
          {stillPending
            ? t("signin.removal.cleanupStillPending", { name: target.name })
            : t("signin.removal.cleanupPending", { name: target.name })}
        </Notice>
        {links ? <Notice tone="info">{linksSummary(links)}</Notice> : null}
        {otherError ? <Notice tone="danger">{otherError.message}</Notice> : null}
        {accountError}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={props.onClose}>
            {t("signin.removal.close")}
          </Button>
          <Button type="button" disabled={busy || waiting} onClick={() => void submit(undefined)}>
            {busy ? t("signin.removal.retrying") : t("signin.removal.retry")}
          </Button>
        </DialogFooter>
        {stepUpDialog}
      </div>
    );
  }

  if (short) {
    // Verified since the list was read: the release needs the full confirmation now.
    const verifiedMeanwhile =
      run.error instanceof ApiError && run.error.code === "confirmation_mismatch";
    return (
      <div className="flex flex-col gap-4">
        {verifiedMeanwhile ? (
          <Notice tone="warning">
            {t("signin.removal.claimVerified", { domain: target.name })}
          </Notice>
        ) : otherError ? (
          <Notice tone="danger">{otherError.message}</Notice>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={props.onClose}>
            {t("signin.removal.keep")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={busy || verifiedMeanwhile}
            onClick={() => void submit(undefined)}
          >
            {busy ? t("signin.removal.cancelling") : t("signin.removal.cancelClaim")}
          </Button>
        </DialogFooter>
      </div>
    );
  }

  // The step-up sits outside the form: a portal's submit still bubbles through React's tree.
  return (
    <>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready && !busy) {
            void submit(typed);
          }
        }}
      >
        {preview.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : preview.isError ? (
          <Notice
            tone="danger"
            action={
              // A domain kept in this preview may have changed meanwhile: back to none kept.
              keep.length > 0 ? (
                <Button type="button" size="sm" variant="outline" onClick={() => setKeep([])}>
                  {t("signin.removal.keepNone")}
                </Button>
              ) : undefined
            }
          >
            {preview.error.message}
          </Notice>
        ) : (
          <>
            <Impact target={target} impact={preview.data} />
            {preview.data.keepableDomains && preview.data.keepableDomains.length > 0 ? (
              <KeepDomains
                impact={preview.data}
                keep={keep}
                disabled={busy}
                onChange={(next) => setKeep([...next].sort())}
              />
            ) : null}
          </>
        )}
        {preview.data?.signsOutActor && !refused ? (
          <Notice tone="warning">
            {target.kind === "idp"
              ? t("signin.removal.signsOutActorIdp", { name: target.name })
              : t("signin.removal.signsOutActorDomain", { domain: target.name })}
          </Notice>
        ) : null}
        {refused ? (
          <Notice tone="danger">
            <span className="font-medium text-danger-fg">
              {target.kind === "idp"
                ? t("signin.removal.refusedIdpTitle", { name: target.name })
                : t("signin.removal.refusedDomainTitle", { domain: target.name })}
            </span>{" "}
            {target.kind === "idp"
              ? t("signin.removal.refusedIdp", { name: target.name })
              : t("signin.removal.refusedDomain")}
          </Notice>
        ) : preview.data ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-confirm`}>
              {t("signin.removal.typeToConfirm", { name: target.name })}
            </Label>
            <Input
              id={`${id}-confirm`}
              autoComplete="off"
              spellCheck={false}
              // Shown once the preview arrived: the next thing to do is to type the name.
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.currentTarget.value)}
            />
            <p className="text-xs text-fg-muted">{t("signin.removal.recentSignIn")}</p>
          </div>
        ) : null}
        {otherError ? <Notice tone="danger">{otherError.message}</Notice> : null}
        {accountError}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={props.onClose}>
            {refused ? t("signin.removal.close") : t("app.cancel")}
          </Button>
          {refused ? null : (
            <Button type="submit" variant="destructive" disabled={!ready || busy || waiting}>
              {busy
                ? t(target.kind === "idp" ? "signin.removal.removing" : "signin.removal.releasing")
                : target.kind === "idp"
                  ? t("signin.removal.removeIdp")
                  : t("signin.removal.releaseDomain")}
            </Button>
          )}
        </DialogFooter>
      </form>
      {stepUpDialog}
    </>
  );
}

/** What a removal reported about its kept domains' links (#419, D11). */
type KeptLinks = { linksSent: number; linksFailed: number };

function linksSummary(links: KeptLinks) {
  return [
    t("signin.removal.linksSent", { count: links.linksSent }),
    links.linksFailed ? t("signin.removal.linksFailed", { count: links.linksFailed }) : null,
  ]
    .filter(Boolean)
    .join(" ");
}

function done(target: RemovalTarget, retried: boolean, links?: KeptLinks | null) {
  if (target.kind === "idp") {
    const removed = retried
      ? t("signin.removal.cleanupDone", { name: target.name })
      : t("signin.removal.idpRemoved", { name: target.name });
    // Said even after a clean-up retry, which no longer reports them.
    return links ? `${removed} ${linksSummary(links)}` : removed;
  }
  return target.verified
    ? t("signin.removal.domainReleased", { domain: target.name })
    : t("signin.removal.claimCancelled", { domain: target.name });
}

const list = (items: readonly string[]) =>
  new Intl.ListFormat(i18n.language, { type: "conjunction" }).format(items);

/** The impact list (the pattern of the file deletion's effects), from the preview only. */
function Impact(props: { target: RemovalTarget; impact: RemovalImpact }) {
  const { target, impact } = props;
  const lines: { key: string; icon: LucideIcon; text: string }[] = [];
  const people =
    impact.people === 0
      ? t("signin.removal.nobodyLoses")
      : [
          t("signin.removal.people", { count: impact.people }),
          impact.createdAtFirstSignIn > 0
            ? t("signin.removal.createdAtFirstSignIn", { count: impact.createdAtFirstSignIn })
            : null,
          target.kind === "idp"
            ? t("signin.removal.accountsStayIdp")
            : t("signin.removal.accountsStayDomain"),
        ]
          .filter(Boolean)
          .join(" ");
  lines.push({ key: "people", icon: UserRound, text: people });
  if (impact.signInContinues) {
    lines.push({
      key: "continues",
      icon: UserRound,
      text: t("signin.removal.signInContinues", {
        count: impact.signInContinues.signedOut,
        name: impact.signInContinues.identityProvider,
      }),
    });
  }
  lines.push({
    key: "sessions",
    icon: MonitorSmartphone,
    text: t("signin.removal.sessionsEnd", {
      sessions: t("signin.removal.sessions", { count: impact.sessions }),
      grants: t("signin.removal.grants", { count: impact.grants }),
    }),
  });
  if (impact.domains.length > 0) {
    lines.push({
      key: "domains",
      icon: Globe,
      text: t("signin.removal.domains", {
        count: impact.domains.length,
        domains: list(impact.domains),
      }),
    });
  }
  for (const [index, source] of impact.directorySources.entries()) {
    lines.push({
      key: `source:${index}`,
      icon: UsersRound,
      text: t("signin.removal.source", {
        name: source.name,
        groups: t("signin.removal.groups", { count: source.groups }),
        memberships: t("signin.removal.memberships", { count: source.memberships }),
      }),
    });
  }
  return (
    <ul
      aria-label={t("signin.removal.impact")}
      className="divide-y divide-line rounded-lg border border-line text-sm text-fg-secondary"
    >
      {lines.map(({ key, icon: Icon, text }) => (
        <li key={key} className="flex gap-2.5 px-3 py-2.5 tabular-nums">
          <Icon
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-fg-muted"
            strokeWidth={1.75}
          />
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Moving off SSO (#419, D11): per verified domain of the IdP, keep it as an ESI-accounts domain
 * and move its people to ESI sign-in, with the server's counts (never names) and the two-step
 * verification setting they then follow.
 */
function KeepDomains(props: {
  impact: RemovalImpact;
  keep: readonly string[];
  disabled: boolean;
  onChange: (next: Set<string>) => void;
}) {
  const id = useId();
  const { base } = useAdminApi();
  const domains = props.impact.keepableDomains ?? [];
  const chosen = new Set(props.keep);
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1 text-sm font-medium text-fg">{t("signin.removal.keepTitle")}</legend>
      {domains.map((d) => (
        <div key={d.id} className="flex items-start gap-2.5">
          <Checkbox
            id={`${id}-${d.id}`}
            className="mt-0.5"
            checked={chosen.has(d.id)}
            disabled={props.disabled}
            aria-describedby={`${id}-${d.id}-counts`}
            onCheckedChange={(on) => {
              const next = new Set(chosen);
              if (on === true) {
                next.add(d.id);
              } else {
                next.delete(d.id);
              }
              props.onChange(next);
            }}
          />
          <div className="flex flex-col gap-0.5">
            <label htmlFor={`${id}-${d.id}`} className="text-sm text-fg">
              {t("signin.removal.keepDomain", { domain: d.domain })}
            </label>
            <p id={`${id}-${d.id}-counts`} className="text-xs text-fg-muted tabular-nums">
              {t("signin.removal.keepCounts", {
                kept: t("signin.removal.keepWithCredential", { count: d.withCredential }),
                links: t("signin.removal.keepLinks", { count: d.withoutCredential }),
              })}
            </p>
          </div>
        </div>
      ))}
      {chosen.size > 0 ? (
        <Notice tone="info">
          {props.impact.keptDomainMfa === "required"
            ? t("signin.removal.keepMfaRequired")
            : t("signin.removal.keepMfaOptional")}{" "}
          <Link
            href={`${adminSettingsPath("security", base)}${settingHash("auth.mfa")}`}
            className="font-medium underline underline-offset-4"
          >
            {t("signin.mode.mfaLink")}
          </Link>
        </Notice>
      ) : null}
    </fieldset>
  );
}
